import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { LOCAL_STORAGE_KEYS } from "../config/local_storage_keys";
import SubscriptionsModal from "../ui/subscriptions/SubscriptionsModal";

interface SubscriptionsModalContextType {
  open: () => void;
  close: () => void;
}

const SubscriptionsModalContext = createContext<
  SubscriptionsModalContextType | undefined
>(undefined);

export function useSubscriptionsModal() {
  const ctx = useContext(SubscriptionsModalContext);
  if (!ctx)
    throw new Error(
      "useSubscriptionsModal must be used within a SubscriptionsModalProvider"
    );
  return ctx;
}

export function SubscriptionsModalProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => {
    // Save the current URL to localStorage when opening the modal
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.SUBSCRIPTIONS_MODAL_RETURN_URL,
      window.location.href
    );
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.SUBSCRIPTIONS_MODAL_RETURN_URL);
    setIsOpen(false);
  }, []);

  // Handle browser back button to close modal instead of navigating away
  useEffect(() => {
    if (!isOpen) return;

    // Push a state to history so back button can be intercepted
    window.history.pushState({ modal: 'subscriptions' }, '');

    const handlePopstate = () => {
      close();
    };

    window.addEventListener('popstate', handlePopstate);

    return () => {
      window.removeEventListener('popstate', handlePopstate);
    };
  }, [isOpen, close]);

  return (
    <SubscriptionsModalContext.Provider value={{ open, close }}>
      {children}
      {isOpen && <SubscriptionsModal onClose={close} />}
    </SubscriptionsModalContext.Provider>
  );
}
